import CommunityDashboardHeader from 'components/community-dashboard-header/CommunityDashboardHeader';
import cc from 'classcat';
import CommunityOverviewPanel from 'components/community-overview-panel/CommunityOverviewPanel';
import {
  MinSidebarCommunityList,
  MinSidebarCommunityListInner,
} from 'components/min-sidebar-community-list/MinSidebarCommunityList';
import PostLoginNavBar from 'components/post-login-nav-bar/PostLoginNavBar';
import SharedLayout from 'components/shared-layout/SharedLayout';
import SpaceExplorerPanel from 'components/space-explorer-panel/SpaceExplorerPanel';
import { DashboardParams } from 'constants/routes';
import React, {
  ReactChild,
  ReactChildren,
  Suspense,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { Link, useParams } from 'react-router-dom';
import C from './DashboardLayout.css';
import isNil from 'lodash/isNil';
import not from 'lodash/negate';
import isEmpty from 'lodash/isEmpty';
import PrimarySpaceSearchInput from 'components/primary-space-search-input/PrimarySpaceSearchInput';
import { useAuthState } from 'utils/auth-state';
import PreLoginNavBar from 'components/prelogin-nav-bar/PreLoginNavBar';
import { Community } from '@cocoplatform/coco-rtc-shared';
import { useCommunityDetails } from 'atoms/community-details';
import { match, P } from 'ts-pattern';
import { useWindowSize } from 'rooks';
import CloseIcon from '@mui/icons-material/Close';
import { NARROW_LAYOUT_THRESHOLD } from 'components/dashboard-layout/breakpoints';
import WorldCorridorPanel from '../world-corridor-panel/WorldCorridorPanel';
import { useUserProfile } from 'atoms/user-profile';
import { Trans } from '@lingui/macro';
import { Stack } from '@mui/material';
import { LocaleSwitcherIcon } from 'components/locale-switcher/LocaleSwitcher';
import { setCommunityIdContext } from 'utils/router-logger';

export default function DashboardLayout(p: {
  community?: Community;
  isOnboarding?: boolean;
  children: ReactChild | ReactChildren;
  hideOverviewPanel?: boolean;
  handle?: string;
}) {
  const routeParams = useParams<DashboardParams>();
  const { userProfile } = useUserProfile();

  const communityId = p.community?.id ?? routeParams.communityId;
  const isCommunitySelected = communityId && communityId !== '$all';
  const { isSignedIn, isProfileUpdated } = useAuthState();
  const { community: communityDetails, syncState } = useCommunityDetails({
    communityId,
    autoLoad: false,
  });
  const { innerWidth } = useWindowSize();
  const isNarrow = !!innerWidth && innerWidth < NARROW_LAYOUT_THRESHOLD;
  const [sbarExpanded, setSbarExpanded] = useState(false);

  useEffect(() => {
    if (!communityId) return;
    setCommunityIdContext(communityId);
  }, [communityId]);

  const toggleSidebar = useCallback(() => {
    setSbarExpanded((s) => !s);
  }, [setSbarExpanded]);

  const minSbar = (
    <div className={C.minSidebar}>
      {isSignedIn && isProfileUpdated ? (
        <MinSidebarCommunityList
          showSidebarExpander={isNarrow}
          onSidebarToggle={toggleSidebar}
          activeCommunityId={communityId}
          showExploreButton={!p.handle}
        />
      ) : p.community ? (
        <MinSidebarCommunityListInner
          activeCommunityId={p.community.id}
          communities={[p.community]}
          showSidebarExpander={isNarrow}
          onSidebarToggle={toggleSidebar}
          showExploreButton={!p.handle}
        />
      ) : null}
    </div>
  );

  const overviewPanel = p.hideOverviewPanel ? null : isCommunitySelected ? (
    <CommunityOverviewPanel
      key={communityId}
      className={C.panel}
      community={communityDetails ?? p.community}
    />
  ) : (
    <WorldCorridorPanel key={communityId} className={C.panel} />
  );

  const mainSbar = p.hideOverviewPanel ? null : (
    <div className={C.mainSidebar}>
      {isNarrow ? (
        <a className={C.sbarCloseBtn} onClick={toggleSidebar}>
          <CloseIcon />
        </a>
      ) : null}
      {isNarrow ? null : overviewPanel}
      {isSignedIn &&
        ((communityDetails?.isActive &&
          communityDetails?.pendingApproval === false) ||
          communityId === '$all') && <SpaceExplorerPanel className={C.panel} />}
    </div>
  );

  const header = match({
    isOnboarding: p.isOnboarding,
    syncState,
    isSignedIn,
    communityId,
    communityDetails: communityDetails,
    hideOverviewPanel: p.hideOverviewPanel,
  })
    .with(
      { hideOverviewPanel: true },
      (): ReactChild => (
        <div className={C.header}>
          <AppInfoHeader />
        </div>
      ),
    )
    .with(
      { isSignedIn: false },
      (): ReactChild => (
        <div className={C.header}>
          <AppInfoHeader />
        </div>
      ),
    )
    .with(
      { communityId: P.union('$all', P.nullish) },
      (): ReactChild => (
        <div className={C.header}>
          <CommunityDashboardHeader />
        </div>
      ),
    )
    .with(
      {
        syncState: P.union('loading', 'pending'),
        communityDetails: P.union(
          {
            roles: P.when(isEmpty),
          },
          P.nullish,
        ),
      },
      () => <></>,
    )
    .with(
      P.union(
        { isOnboarding: true },
        {
          communityDetails: P.union(
            { deactivatedAt: P.when(not(isNil)) },
            { pendingApproval: true },
            // { isActive: false },
          ),
        },
      ),
      () => <></>,
    )
    .with(P.union({ communityDetails: { roles: P.when(isEmpty) } }), () => (
      <div className={C.header}>
        <AppInfoHeader />
      </div>
    ))
    .otherwise(() => (
      <div className={C.header}>
        <CommunityDashboardHeader />
      </div>
    ));

  const body = (
    <div className={cc([C.body, 'dashboard-body'])}>
      {isNarrow ? overviewPanel : null}
      <Suspense fallback={null}>{p.children}</Suspense>
    </div>
  );

  return (
    <SharedLayout
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
      }}
      header={
        isSignedIn && userProfile?.onboardingComplete ? (
          <PostLoginNavBar
            borderDist={isNarrow ? 8 : 20}
            centerControls={match({ communityId, communityDetails, syncState })
              .with(
                { communityId: '$all' },
                (): ReactChild => <PrimarySpaceSearchInput />,
              )
              .with(
                {
                  communityDetails: P.nullish,
                  syncState: P.union('loading', 'pending'),
                },
                (): null => null,
              )
              .with(
                {
                  communityDetails: P.union(
                    { pendingApproval: true },
                    { deactivatedAt: P.when(not(isNil)) },
                    { roles: P.when(isEmpty) },
                  ),
                },
                (): null => null,
              )
              .otherwise(
                (): ReactChild => (
                  <PrimarySpaceSearchInput />
                ),
              )}
          />
        ) : (
          <PreLoginNavBar
            borderDist={20}
            rightControls={
              <Stack alignItems='center' direction='row' spacing={2}>
                <Link
                  to='/?home=1'
                  target='_blank'
                  style={{
                    marginRight: '0.5rem',
                    color: 'var(--color-base)',
                    fontSize: 'var(--font-size-secondary)',
                    fontWeight: 'bold',
                  }}
                >
                  <Trans>About</Trans>
                </Link>
                {p.handle && (
                  <Link
                    to={`/app/signin`}
                    style={{
                      color: 'var(--color-base)',
                      fontSize: 'var(--font-size-secondary)',
                      fontWeight: 'bold',
                    }}
                  >
                    <Trans>Sign In</Trans>
                  </Link>
                )}
                <LocaleSwitcherIcon showLanguageName />
              </Stack>
            }
          />
        )
      }
      body={
        <div
          className={cc([
            C.container,
            {
              narrow: isNarrow,
              [C.narrow]: isNarrow,
              [C.hideOverviewPanel]: p.hideOverviewPanel,
            },
          ])}
        >
          {minSbar}
          {isNarrow ? (
            sbarExpanded ? (
              mainSbar
            ) : (
              <div className={C.narrowBodyWrapper}>
                {header}
                {body}
              </div>
            )
          ) : (
            <>
              {mainSbar}
              {header}
              {body}
            </>
          )}
        </div>
      }
    />
  );
}

const AppInfoHeader = () => (
  <div className={C.appInfoHeader}>
    <Trans>
      CoCo is a live co-creative platform for young people, developed at MIT.
    </Trans>{' '}
    <a className={C.inlineLink} href='/' target='_blank'>
      <Trans>Learn more.</Trans>
    </a>
  </div>
);
